import { computed } from 'mobx';
import { get, kebabcase } from 'lodash';
import moment from 'moment';
import { getIssueMetrics } from '@kentik/ui-shared/synthetics/utils';
import { Model } from 'core/model';
import { getTarget } from 'app/stores/synthetics/utils';
import { getTestResultsLink } from 'app/views/synthetics/utils/alarmsTableRenderers';

export default class AlarmTriggerModel extends Model {
  @computed
  get metrics() {
    return getIssueMetrics(this.get('issue'));
  }

  @computed
  get metricNames() {
    return this.metrics.map(({ metric }) => metric);
  }

  @computed
  get metricClassNames() {
    return this.metricNames.map((metric) => kebabcase(metric?.toLowerCase()));
  }

  @computed
  get agentId() {
    return this.get('agentId') || this.collection.agentId || null;
  }

  @computed
  get target() {
    return get(this.get('issue'), 'value.Target') || this.collection.target || null;
  }

  @computed
  get targetAgentId() {
    return get(this.get('issue'), 'value.TargetAgent') || this.collection.targetAgentId || null;
  }

  @computed
  get targetAgent() {
    return this.store.$syn.agents.get(this.targetAgentId);
  }

  @computed
  get resolver() {
    return get(this.get('issue'), 'value.Resolver') || this.collection.resolver || null;
  }

  @computed
  get testResultsTarget() {
    const {
      collection: { test }
    } = this;

    if (test?.isDns) {
      return this.resolver;
    }

    return getTarget(test, this.targetAgentId || this.target);
  }

  @computed
  get hasCharts() {
    return (
      this.agentId &&
      this.testResultsTarget &&
      this.metricClassNames.some(
        (className) =>
          [
            'latency',
            'jitter',
            'packet-loss',
            'http-status-code',
            'dns-result-ips',
            'dns-resolution-time',
            'throughput',
            'throughput-bandwidth',
            'transaction-status-health'
          ].indexOf(className) > -1
      )
    );
  }

  @computed
  get hasExpandableCharts() {
    return !this.collection.test?.isPerAgentAlerting && this.hasCharts;
  }

  @computed
  get timestampMs() {
    return moment(this.get('timestamp')).unix();
  }

  @computed
  get detailsUrl() {
    const { collection } = this;

    return getTestResultsLink({
      test: collection.test,
      agentId: this.agentId,
      targetAgentId: this.testResultsTarget,
      target: this.testResultsTarget,
      startTime: moment.unix(collection.testResultsStartTime).toISOString(),
      endTime: this.get('timestamp')
    }).testUrl;
  }
}
